import { Badge, Button, Flex, Table, Text, Dialog } from "@radix-ui/themes";
import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { DropdownMenu } from "@/components/shared/dropdown-menu";
import {
  DotsVerticalIcon,
  Pencil1Icon,
  TrashIcon,
  ArrowLeftIcon,
} from "@radix-ui/react-icons";
import { Search } from "@/components/shared/inputs/search";
import { ExpandableText } from "@/v2/components/expandableText/ExpandableText";
import { useRegressionApi } from "@/v2/entities/regression/Regression.api";
import { useMutation, useQuery } from "react-query";
import {
  CreateRegressionSetItemPayload,
  RegressionSetItem,
  UpdateRegressionSetItemPayload,
} from "@/v2/entities/regression/Regression.model";

export function RegressionSetDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const {
    getRegressionSet,
    createRegressionSetItem,
    updateRegressionSetItem,
    deleteRegressionSetItem,
  } = useRegressionApi();

  const {
    data: regressionSet,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["regression-set", id],
    queryFn: () => getRegressionSet(id!),
    enabled: !!id,
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [question, setQuestion] = useState("");
  const [expectedAnswer, setExpectedAnswer] = useState("");
  const [searchPhrase, setSearchPhrase] = useState("");
  const createItemMutation = useMutation({
    mutationKey: ["create-regression-set-item"],
    mutationFn: (input: CreateRegressionSetItemPayload) =>
      createRegressionSetItem(id!, input),
  });

  const handleCreateItem = async () => {
    try {
      await createItemMutation.mutateAsync({
        question,
        expectedAnswer,
      });

      toast("Item added successfully");
      setDialogOpen(false);
      setQuestion("");
      setExpectedAnswer("");
      refetch();
    } catch (error) {
      toast.error("Failed to add item");
    }
  };

  if (isLoading) {
    return (
      <Flex align="center" justify="center" py="8">
        <Text>Loading...</Text>
      </Flex>
    );
  }

  if (!regressionSet) {
    return (
      <Flex align="center" justify="center" py="8" direction="column" gap="2">
        <Text>Regression set not found</Text>
        <Button onClick={() => navigate("/automation-center/regression-sets")}>
          Back to Regression Sets
        </Button>
      </Flex>
    );
  }

  return (
    <Flex direction={"column"} gap={"8"} px={"8"} py={"8"}>
      <Flex direction={"row"} gap={"2"} justify={"between"}>
        <Flex direction={"column"} gap={"2"}>
          <Flex align="center" gap="2">
            <Button
              variant="ghost"
              size="1"
              onClick={() => navigate("/automation-center/regression-sets")}
            >
              <ArrowLeftIcon />
            </Button>
            <h3 style={{ fontWeight: "500" }}>{regressionSet.name}</h3>
          </Flex>
          {regressionSet.description && (
            <Text size="2" color="gray">
              {regressionSet.description}
            </Text>
          )}
        </Flex>
        <Dialog.Root open={dialogOpen} onOpenChange={setDialogOpen}>
          <Dialog.Trigger>
            <Button highContrast color="gray">
              Add Item
            </Button>
          </Dialog.Trigger>

          <Dialog.Content className="w-[500px]">
            <Dialog.Title>Add New Item</Dialog.Title>
            <Dialog.Description size="2" mb="4">
              Add a question and expected answer to this regression set
            </Dialog.Description>

            <Flex direction="column" gap="3">
              <label>
                <Text as="div" size="2" mb="1" weight="bold">
                  Question
                </Text>
                <textarea
                  className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gray-200"
                  placeholder="Enter the test question"
                  rows={3}
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                />
              </label>

              <label>
                <Text as="div" size="2" mb="1" weight="bold">
                  Expected Answer
                </Text>
                <textarea
                  className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gray-200"
                  placeholder="Enter the expected answer"
                  rows={4}
                  value={expectedAnswer}
                  onChange={(e) => setExpectedAnswer(e.target.value)}
                />
              </label>
            </Flex>

            <Flex gap="3" mt="4" justify="end">
              <Dialog.Close>
                <Button variant="soft" color="gray">
                  Cancel
                </Button>
              </Dialog.Close>
              <Button
                color="gray"
                highContrast
                onClick={handleCreateItem}
                disabled={
                  !question.trim() ||
                  !expectedAnswer.trim() ||
                  createItemMutation.isLoading
                }
              >
                {createItemMutation.isLoading ? "Adding..." : "Add Item"}
              </Button>
            </Flex>
          </Dialog.Content>
        </Dialog.Root>
      </Flex>

      <Flex direction={"column"} gap={"2"}>
        <Flex direction={"row"} gap={"2"}>
          <Flex gap={"2"} align={"center"}>
            <Text size={"3"} weight={"medium"}>
              Items
            </Text>
            <Badge color="gray">{regressionSet.items?.length || 0}</Badge>
          </Flex>
          <Search
            initialValue={searchPhrase}
            placeholder="Search items"
            setSearchPhrase={setSearchPhrase}
            debounce={50}
          />
        </Flex>

        <Table.Root>
          <Table.Header>
            <Table.Row className=" text-gray-500 font-normal">
              <Table.ColumnHeaderCell className=" font-normal w-2/5">
                Question
              </Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell className=" font-normal w-2/5">
                Expected Answer
              </Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell className=" font-normal w-1/6">
                Created
              </Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell className=" font-normal w-1/12"></Table.ColumnHeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {(!regressionSet.items || regressionSet.items.length === 0) && (
              <Table.Row>
                <Table.Cell colSpan={4}>
                  <Flex
                    align="center"
                    justify="center"
                    py="6"
                    direction="column"
                    gap="2"
                  >
                    <Text size="2" color="gray">
                      No items found
                    </Text>
                    <Text size="2" color="gray">
                      Add questions and expected answers to get started
                    </Text>
                    <Button
                      color="gray"
                      highContrast
                      onClick={() => setDialogOpen(true)}
                      className="mt-2"
                    >
                      Add Item
                    </Button>
                  </Flex>
                </Table.Cell>
              </Table.Row>
            )}
            {regressionSet.items
              ?.filter(
                (item) =>
                  item.question
                    .toLowerCase()
                    .includes(searchPhrase.toLowerCase()) ||
                  item.expectedAnswer
                    .toLowerCase()
                    .includes(searchPhrase.toLowerCase())
              )
              .map((item) => (
                <RegressionSetItemRow
                  key={item.id}
                  item={item}
                  refetch={refetch}
                />
              ))}
          </Table.Body>
        </Table.Root>
      </Flex>
    </Flex>
  );
}

interface RegressionSetItemRowProps {
  item: RegressionSetItem;
  refetch: () => void;
}

function RegressionSetItemRow({ item, refetch }: RegressionSetItemRowProps) {
  const { deleteRegressionSetItem, updateRegressionSetItem } =
    useRegressionApi();
  const deleteItemMutation = useMutation({
    mutationKey: ["delete-regression-set-item"],
    mutationFn: () => deleteRegressionSetItem(item.id),
  });
  const updateItemMutation = useMutation({
    mutationKey: ["update-regression-set-item"],
    mutationFn: (input: UpdateRegressionSetItemPayload) =>
      updateRegressionSetItem(item.id, input),
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [question, setQuestion] = useState(item.question);
  const [expectedAnswer, setExpectedAnswer] = useState(item.expectedAnswer);

  const handleUpdateItem = async () => {
    try {
      await updateItemMutation.mutateAsync({
        question,
        expectedAnswer,
      });

      toast("Item updated successfully");
      setDialogOpen(false);
      refetch();
    } catch (error) {
      toast.error("Failed to update item");
    }
  };

  return (
    <Table.Row>
      <Table.Cell>
        <ExpandableText text={item.question} />
      </Table.Cell>
      <Table.Cell>
        <ExpandableText text={item.expectedAnswer} />
      </Table.Cell>
      <Table.Cell>{new Date(item.createdAt).toLocaleDateString()}</Table.Cell>
      <Table.Cell>
        <Dialog.Root open={dialogOpen} onOpenChange={setDialogOpen}>
          <DropdownMenu
            items={[
              {
                label: "Edit",
                icon: <Pencil1Icon />,
                onClick: () => setDialogOpen(true),
              },
              {
                label: deleteItemMutation.isLoading ? "Deleting..." : "Delete",
                icon: <TrashIcon />,
                onClick: async () => {
                  if (deleteItemMutation.isLoading) return;
                  try {
                    await deleteItemMutation.mutateAsync();
                    toast("Item deleted successfully");
                    refetch();
                  } catch (error) {
                    toast.error("Failed to delete item");
                  }
                },
              },
            ]}
          >
            <DropdownMenu.Trigger>
              <Button variant="ghost" size="1" color="gray" highContrast>
                <DotsVerticalIcon />
              </Button>
            </DropdownMenu.Trigger>
          </DropdownMenu>

          <Dialog.Content className="w-[500px]">
            <Dialog.Title>Edit Item</Dialog.Title>
            <Dialog.Description>
              Update the question and expected answer.
            </Dialog.Description>

            <Flex direction="column" gap="3">
              <label>
                <Text as="div" size="2" mb="1" weight="bold">
                  Question
                </Text>
                <textarea
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2"
                  rows={3}
                />
              </label>

              <label>
                <Text as="div" size="2" mb="1" weight="bold">
                  Expected Answer
                </Text>
                <textarea
                  value={expectedAnswer}
                  onChange={(e) => setExpectedAnswer(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2"
                  rows={4}
                />
              </label>
            </Flex>

            <Flex gap="3" mt="4" justify="end">
              <Dialog.Close>
                <Button variant="soft" color="gray">
                  Cancel
                </Button>
              </Dialog.Close>
              <Button
                color="gray"
                highContrast
                onClick={handleUpdateItem}
                disabled={updateItemMutation.isLoading}
              >
                {updateItemMutation.isLoading ? "Updating..." : "Update Item"}
              </Button>
            </Flex>
          </Dialog.Content>
        </Dialog.Root>
      </Table.Cell>
    </Table.Row>
  );
}
